import React, { useEffect, useState } from 'react';
import { Clock, ArrowRight, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрого выбора часовых поясов
 */
const QuickSelectSidebar = ({ 
  formData,
  setFormData,
  russianCities,
  worldCities,
  errors,
  isCalculating
}) => {
  const [currentTime, setCurrentTime] = useState('');
  const [currentMoscowTime, setCurrentMoscowTime] = useState('');
  
  // Обновляем текущее время каждую секунду
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      
      // Локальное время
      const options = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };
      setCurrentTime(new Intl.DateTimeFormat('ru-RU', options).format(now));
      
      // Московское время
      const moscowOptions = {
        timeZone: 'Europe/Moscow',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };
      setCurrentMoscowTime(new Intl.DateTimeFormat('ru-RU', moscowOptions).format(now));
    };
    
    // Первое обновление
    updateTime();
    
    // Настраиваем интервал
    const intervalId = setInterval(updateTime, 1000);
    
    // Очищаем интервал при размонтировании
    return () => clearInterval(intervalId);
  }, []);
  
  // Быстрое переключение между парами городов
  const quickPairs = [
    { source: 'Moscow', target: 'New_York', name: 'Москва — Нью-Йорк' },
    { source: 'Moscow', target: 'London', name: 'Москва — Лондон' },
    { source: 'Moscow', target: 'Beijing', name: 'Москва — Пекин' },
    { source: 'Moscow', target: 'Tokyo', name: 'Москва — Токио' },
    { source: 'Saint_Petersburg', target: 'Paris', name: 'Санкт-Петербург — Париж' },
    { source: 'Vladivostok', target: 'Moscow', name: 'Владивосток — Москва' }
  ];
  
  // Переключение между городами и выполнение расчета
  const handleQuickSelect = (source, target) => {
    setFormData(prev => ({
      ...prev,
      sourceCity: source,
      targetCity: target,
      sourceTimezone: russianCities.concat(worldCities).find(city => city.id === source)?.timezone || prev.sourceTimezone,
      targetTimezone: russianCities.concat(worldCities).find(city => city.id === target)?.timezone || prev.targetTimezone,
      view: 'converter', // Переключаемся на вид конвертера
    }));
    
    // Выполняем расчет автоматически после выбора пары
    setTimeout(() => {
      document.querySelector('button[type="submit"]')?.click();
    }, 100);
  };
  
  // Расчет времени
  const calculateTime = () => {
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Переключение между разными видами
  const switchView = (view) => {
    setFormData(prev => ({
      ...prev,
      view
    }));
    
    // Выполняем расчет автоматически после смены вида
    setTimeout(() => {
      document.querySelector('button[type="submit"]')?.click();
    }, 100);
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Clock className="w-5 h-5 mr-2 text-purple-500" />
        Быстрый выбор
      </h3>
      
      <div className="space-y-5">
        {/* Текущее время */}
        <div className="p-3 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-lg border border-purple-100 dark:border-purple-800 text-center">
          <div className="text-sm text-purple-600 dark:text-purple-400 mb-1">Ваше локальное время</div>
          <div className="text-2xl font-mono font-bold text-purple-800 dark:text-purple-200 clock-display">
            {currentTime}
          </div>
          <div className="text-xs text-purple-500 dark:text-purple-400 mt-1 flex justify-center items-center gap-1">
            <span>Москва:</span>
            <span className="font-medium">{currentMoscowTime}</span>
          </div>
        </div>
        
        {/* Быстрый выбор типа представления */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Выберите представление
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => switchView('converter')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.view === 'converter' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Конвертер</div>
              <div className="text-xs opacity-75">Перевод времени</div>
            </button>
            <button
              type="button"
              onClick={() => switchView('worldclock')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.view === 'worldclock' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Мировые часы</div>
              <div className="text-xs opacity-75">Несколько городов</div>
            </button>
            <button
              type="button"
              onClick={() => switchView('meeting')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.view === 'meeting' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Планировщик</div>
              <div className="text-xs opacity-75">Организация встреч</div>
            </button>
            <button
              type="button"
              onClick={() => switchView('difference')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.view === 'difference' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Разница</div>
              <div className="text-xs opacity-75">Между городами</div>
            </button>
          </div>
        </div>
        
        {/* Популярные пары часовых поясов для быстрого выбора */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Популярные пары городов
          </div>
          <div className="space-y-1">
            {quickPairs.map((pair, idx) => (
              <button
                key={idx}
                onClick={() => handleQuickSelect(pair.source, pair.target)}
                className="block w-full text-left p-2 text-sm rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800 dark:text-gray-200">{pair.name}</span>
                  <ArrowRight className="w-4 h-4 text-purple-500" />
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Быстрый выбор пары часовых поясов
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTime}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать время
        </Button>
      </div>
    </div>
  );
};

export default QuickSelectSidebar;